import React, { useState, useEffect, useRef } from 'react';
import {
    ShoppingCart, Users, Receipt,
    Target, TrendingUp, ChevronDown, RefreshCw, PieChart,
} from 'lucide-react';
import { COR, FONTE_DADOS, SOMBRA, RAIO, cartao, MultiSelectLojas, SetaChip, LinhaMercado, fmt1, fmtMoeda2, fmtMoedaCompacta, fmtNumCompacto, CARD_PAD, CARD_MIN_H, CARD_GAP, rotuloCard, valorCard, rodapeCard, raizDashboard, OverlayCarregando, conteudoCarregando, TituloBloco } from './ui';
import DashboardRH from './DashboardRH';

// =====================================================================
// DASHBOARD DE OPERAÇÃO — o corpo das abas **Operação** e **RH** da Home. Qual
// das duas está na tela vem da prop `visao`, controlada pela página; quem alcança
// cada uma é o `abas-home.js`.
// =====================================================================
// Três blocos, todos "loja em foco vs mercado (média da rede)":
//   1. Cards: Venda, GC, Ticket Médio (período MTD, variação YoY).
//   2. Tabela "Resumo" por segmento (réplica do BI MCDES_00 › aba Resumo).
//   3. Avaliações: nota final dos módulos (Auditoria SA, TSV, Auditoria/Risco,
//      BSV, Certificação) da loja vs a média do mercado.
// Consome /api/home/operacao/vendas e /operacao/avaliacoes. Toggle Operação|RH
// (RH é placeholder "Em breve" — spec da visão RH virá depois).
// Estilos inline (convenção do projeto), paleta em ./ui.js.

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

// Aparência dos filtros enquanto as queries rodam: cinza e cursor de espera —
// o `disabled` já trava o clique, isto só deixa a trava VISÍVEL.
const selectTravado = { backgroundColor: '#f0eeee', color: '#9a9a9a', cursor: 'wait' };

// Formatadores pt-BR (fmt1/fmtMoeda2 + os compactos) centralizados em ./ui.js.

// SetaChip (chip de tendência ▲/▼/–) centralizado em ./ui.js — compartilhado com o RH.

// --- Chip de desvio do realizado vs um alvo (projeção ou orçamento/meta) ------
// Regra pedida (2026-07-07): o card mostra o DESVIO (1 − realizado/alvo) × 100 —
// o quanto o realizado ficou ABAIXO (positivo) ou ACIMA (negativo) do alvo —, não
// o atingimento cru. O backend manda `atingido` (= realizado ÷ alvo × 100); o
// desvio é 100 − atingido. Seta/cor seguem a direção BOA: acima do alvo = ▲ verde,
// abaixo = ▼ vermelho. `alvo` entra no "abaixo/acima da ___"; `alvoVazio` no "sem
// ___ no período".
function ChipDesvioAlvo({ atingido, alvo, alvoVazio }) {
    if (atingido == null) {
        return <span style={{ ...rodapeCard, color: '#bbb' }}>sem {alvoVazio} no período</span>;
    }
    const desvio = 100 - atingido;   // >0 = abaixo do alvo · <0 = acima · 0 = em cima
    const mag = Math.abs(desvio).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
    const texto = desvio === 0 ? `na ${alvo}` : `${mag}% ${desvio > 0 ? 'abaixo' : 'acima'} da ${alvo}`;
    return <SetaChip delta={atingido - 100} bom={atingido >= 100} texto={texto} />;
}

// --- Card de indicador (Venda / GC / Ticket Médio) ---------------------------
// evolYoY      = variação % da LOJA vs mesmo período do ano anterior.
// mercadoEvol  = a MESMA variação, do mercado (a rede fora o foco) — os dois na
//   mesma régua, para responder "cresci mais ou menos que o mercado?". Antes esta
//   linha trazia o VALOR médio do mercado (Julia, 2026-07-21 — ver LinhaMercado).
//   null na visão Rede: a rede é o próprio mercado, não há com quem comparar.
// Nos três, "maior é melhor" — venda, GC e ticket subindo é bom nos dois lados.
function CardIndicador({ icone, rotulo, valor, evolYoY, mercadoEvol, cor }) {
    return (
        <div style={{ ...cartao, padding: CARD_PAD, display: 'flex', flexDirection: 'column', gap: CARD_GAP, minHeight: CARD_MIN_H }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: cor }}>
                {icone}
                <span style={rotuloCard}>{rotulo}</span>
            </div>
            <div style={{ ...valorCard, color: COR.texto }}>{valor}</div>
            {/* Variação vs ano anterior (chip com seta) */}
            {evolYoY != null && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <SetaChip delta={evolYoY} bom={evolYoY > 0} texto={`${Math.abs(evolYoY).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% vs ano ant.`} />
                </div>
            )}
            {/* Variação do mercado no mesmo período (só no foco de uma loja). */}
            <LinhaMercado variacao={mercadoEvol} />
        </div>
    );
}

// --- Card de Orçamento: meta do período + desvio do realizado ----------------
// Mostra o orçado e o quanto a venda ficou abaixo/acima da meta (ver ChipDesvioAlvo).
function CardOrcamento({ orcamento }) {
    const { valor, atingido } = orcamento;
    return (
        <div style={{ ...cartao, padding: CARD_PAD, display: 'flex', flexDirection: 'column', gap: CARD_GAP, minHeight: CARD_MIN_H }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: COR.amarelo }}>
                <Target size={18} />
                <span style={rotuloCard}>ORÇAMENTO</span>
            </div>
            <div style={{ ...valorCard, color: COR.texto }}>{fmtMoedaCompacta(valor)}</div>
            <ChipDesvioAlvo atingido={atingido} alvo="meta" alvoVazio="orçado" />
        </div>
    );
}

// --- Card de Projeção: estimativa do período + desvio do realizado -----------
// Irmão do CardOrcamento: mostra o valor projetado e o quanto a venda ficou
// abaixo/acima da projeção (ver ChipDesvioAlvo). Fica entre Venda e Orçamento.
function CardProjecao({ projecao }) {
    const { valor, atingido } = projecao;
    return (
        <div style={{ ...cartao, padding: CARD_PAD, display: 'flex', flexDirection: 'column', gap: CARD_GAP, minHeight: CARD_MIN_H }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: COR.vermelhoEscuro }}>
                <TrendingUp size={18} />
                <span style={rotuloCard}>PROJEÇÃO</span>
            </div>
            <div style={{ ...valorCard, color: COR.texto }}>{fmtMoedaCompacta(valor)}</div>
            <ChipDesvioAlvo atingido={atingido} alvo="projeção" alvoVazio="projeção" />
        </div>
    );
}

// --- Tabela "Resumo" por segmento --------------------------------------------
function TabelaSegmentos({ segmentos }) {
    const th = { padding: '10px 12px', fontSize: '12px', fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap' };
    const thEsq = { ...th, textAlign: 'left' };
    const td = { padding: '9px 12px', fontSize: '13px', textAlign: 'right', whiteSpace: 'nowrap', color: COR.texto };
    const tdEsq = { ...td, textAlign: 'left', fontWeight: 600 };

    // Célula de evolução com seta colorida (venda/GC/TM: subir é bom).
    const cellEvol = (v) => (
        <td style={td}>
            <SetaChip delta={v} bom={v > 0} texto={`${Math.abs(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`} />
        </td>
    );

    // Esconde segmentos sem movimento no período (venda E GC zerados) — ex.: ao
    // focar a ANV, some Dessert/Quiosque/Café/Delivery, que ela não opera. A linha
    // Total nunca é filtrada (mesmo em foco de loja pequena). Não mexe na conta de
    // participação: linha zerada contribui 0, então o Total segue 100%.
    const visiveis = segmentos.filter((s) => s.segmento === 'Total' || s.vendas !== 0 || s.gc !== 0);

    return (
        <div style={{ ...cartao, padding: '4px', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ backgroundColor: COR.vermelhoEscuro, color: '#fff' }}>
                        <th style={thEsq}>Segmento</th>
                        <th style={th}>Vendas</th>
                        <th style={th}>% Ev. Vendas</th>
                        <th style={th}>GC</th>
                        <th style={th}>% Ev. GC</th>
                        <th style={th}>Ticket Médio</th>
                        <th style={th}>% Ev. TM</th>
                        <th style={th}>Participação</th>
                    </tr>
                </thead>
                <tbody>
                    {visiveis.map((s, i) => {
                        const ehTotal = s.segmento === 'Total';
                        const fundo = ehTotal ? COR.amareloFundo : (i % 2 ? '#faf7f7' : '#fff');
                        const peso = ehTotal ? 700 : 400;
                        return (
                            <tr key={s.segmento} style={{ backgroundColor: fundo, borderTop: ehTotal ? `2px solid ${COR.bordaSuave}` : 'none' }}>
                                <td style={{ ...tdEsq, fontWeight: ehTotal ? 800 : 600 }}>{s.segmento}</td>
                                <td style={{ ...td, fontWeight: peso }}>{fmtMoedaCompacta(s.vendas)}</td>
                                {cellEvol(s.evolVendas)}
                                <td style={{ ...td, fontWeight: peso }}>{fmtNumCompacto(s.gc)}</td>
                                {cellEvol(s.evolGc)}
                                <td style={{ ...td, fontWeight: peso }}>{fmtMoeda2(s.ticketMedio)}</td>
                                {cellEvol(s.evolTm)}
                                <td style={{ ...td, fontWeight: peso }}>{fmt1(s.participacao)}%</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// --- Combobox suspenso de dias (multi-seleção) -------------------------------
// Vazio = mês inteiro; marque 1 ou vários dias avulsos (não precisam ser
// contíguos). Fecha ao clicar fora. Grade de dias no estilo mini-calendário.
function MultiSelectDias({ dias, selecionados, onChange, selectStyle, desabilitado }) {
    const [aberto, setAberto] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, []);
    // Carregou com a grade de dias aberta? Fecha — o botão trava desabilitado e a
    // grade ficaria pendurada, aberta e sem reagir a clique.
    useEffect(() => { if (desabilitado) setAberto(false); }, [desabilitado]);
    const toggle = (d) => onChange(
        selecionados.includes(d) ? selecionados.filter((x) => x !== d) : [...selecionados, d].sort((a, b) => a - b)
    );
    const resumo = selecionados.length === 0 ? 'Todos os dias'
        : selecionados.length === 1 ? `Dia ${String(selecionados[0]).padStart(2, '0')}`
            : `${selecionados.length} dias`;
    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setAberto((a) => !a)} disabled={desabilitado}
                style={{ ...selectStyle, ...(desabilitado ? selectTravado : null), minWidth: '132px', display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                {resumo} <ChevronDown size={14} />
            </button>
            {aberto && (
                <div style={{ position: 'absolute', zIndex: 30, top: 'calc(100% + 4px)', left: 0, backgroundColor: '#fff', border: `1px solid ${COR.bordaSuave}`, borderRadius: RAIO, boxShadow: SOMBRA, padding: '10px', width: '236px' }}>
                    <button type="button" onClick={() => onChange([])}
                        style={{ width: '100%', textAlign: 'left', border: 'none', background: 'none', cursor: 'pointer', fontFamily: FONTE_DADOS, fontSize: '12px', fontWeight: 700, color: COR.vermelhoEscuro, padding: '2px 4px 8px' }}>
                        Limpar (mês inteiro)
                    </button>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '5px', maxHeight: '190px', overflowY: 'auto' }}>
                        {dias.map((d) => {
                            const on = selecionados.includes(d);
                            return (
                                <button key={d} type="button" onClick={() => toggle(d)}
                                    style={{ border: `1px solid ${on ? COR.vermelhoEscuro : COR.bordaSuave}`, backgroundColor: on ? COR.vermelhoEscuro : '#fff', color: on ? '#fff' : COR.texto, borderRadius: '6px', padding: '6px 0', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: FONTE_DADOS }}>
                                    {String(d).padStart(2, '0')}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// --- Combobox suspenso de LOJAS (multi-seleção, só escritório) ----------------
// O FOCO da análise: vazio = Rede (visão geral); marque 1 ou várias lojas. Com
// lojas marcadas, os cards mostram a MÉDIA POR LOJA das marcadas vs o mercado (a
// rede, sem a SED enquanto for um subconjunto). Mesmo padrão do MultiSelectDias.
//
// Marcar/desmarcar NÃO recarrega a tela: escreve no rascunho de filtros da página,
// que só vira consulta no botão "Aplicar" (ver o cabeçalho do DashboardOperacao).
// ⚠️ `MultiSelectLojas` MUDOU-SE PARA ./ui.js em 11/09/2026, quando virou o
//    segundo consumidor: a aba Escritório da Home passou a filtrar pendências por
//    loja e precisa do MESMO seletor. Duas cópias divergiriam em silêncio — uma
//    ordenando a seleção e a outra não, por exemplo — e o usuário veria dois
//    filtros de loja com comportamentos diferentes na mesma tela.


// --- Botão "Aplicar" — o ÚNICO gatilho de carga da tela ----------------------
// `sujo` = há filtro mexido e não aplicado. Quando limpo, o botão desbota e trava:
// clicar refaria exatamente a consulta que já está na tela. Quando sujo, fica
// vermelho e chama o clique — é o sinal de "tem filtro te esperando".
function BotaoAplicar({ sujo, onClick, desabilitado }) {
    const inerte = desabilitado || !sujo;
    return (
        <button type="button" onClick={onClick} disabled={inerte}
            title={sujo ? 'Carregar os dados com os filtros escolhidos' : 'Os filtros escolhidos já são os que estão na tela'}
            style={{
                fontFamily: FONTE_DADOS, fontSize: '13px', fontWeight: 800, letterSpacing: '.02em',
                border: 'none', borderRadius: '8px', padding: '8px 18px',
                cursor: desabilitado ? 'wait' : (sujo ? 'pointer' : 'default'),
                backgroundColor: sujo ? COR.vermelhoEscuro : '#efecec',
                color: sujo ? '#fff' : '#9a9a9a',
                boxShadow: sujo ? SOMBRA : 'none',
            }}>
            Aplicar
        </button>
    );
}

// =====================================================================
// Filtros da tela num objeto só — é o que viaja entre RASCUNHO e APLICADO.
// `mes` '' = ano inteiro; `dias` [] = mês inteiro; `lojas` [] = rede toda.
const filtrosIniciais = () => {
    const hoje = new Date();
    // Default = mês corrente (bate com o default do backend).
    return { ano: hoje.getFullYear(), mes: hoje.getMonth() + 1, dias: [], lojas: [] };
};
// Dois conjuntos de filtro são a MESMA consulta? Compara conteúdo (os arrays são
// recriados a cada clique, então comparar por identidade acusaria diferença falsa).
const mesmosFiltros = (a, b) => (
    a.ano === b.ano && a.mes === b.mes
    && a.dias.length === b.dias.length && a.dias.every((d, i) => d === b.dias[i])
    && a.lojas.length === b.lojas.length && a.lojas.every((l, i) => l === b.lojas[i])
);

// `visao` ('operacao' | 'rh'): CONTROLADA pela Home desde 11/09/2026, quando as
// duas Homes viraram uma só e a barra de abas subiu para o nível da página (ver
// `abas-home.js`). Antes a visão era estado DAQUI, com um toggle desenhado no
// cabeçalho e uma prop `soRh` para o perfil que só alcança RH.
//
// ⚠️ Quem decide se o usuário PODE estar nesta visão é o `abas-home.js`, não este
//    componente: ele desenha o que lhe pedirem. Um `visao='operacao'` chegando
//    aqui para um perfil de RH não é barrado — a rota do backend também não
//    barraria (só tem `authenticateToken`). A trava é a lista de abas, lá.
export default function DashboardOperacao({ visao = 'operacao' }) {
    // APLICADO = o recorte que está na tela; é ele (e só ele) que dispara a carga.
    const [filtros, setFiltros] = useState(filtrosIniciais);
    // RASCUNHO = o que o usuário está escolhendo. Mexer aqui não consulta nada:
    // vira consulta no botão "Aplicar" (Julia, 2026-07-21). Antes cada clique em
    // qualquer filtro disparava uma rodada nova no servidor — montar "3 dias de 5
    // lojas" custava 8 recargas, das quais 7 eram jogadas fora antes de serem lidas.
    const [rascunho, setRascunho] = useState(filtrosIniciais);
    const { ano, mes, dias: diasSel, lojas: lojasSel } = filtros;
    // Atalho para mexer num campo do rascunho sem repetir o spread.
    const mexer = (campo, valor) => setRascunho((r) => ({ ...r, [campo]: valor }));
    const sujo = !mesmosFiltros(rascunho, filtros);
    const [vendas, setVendas] = useState(null);
    const [rhData, setRhData] = useState(null);
    const [carregando, setCarregando] = useState(true);

    useEffect(() => {
        let vivo = true;
        (async () => {
            setCarregando(true);
            const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
            const qp = new URLSearchParams();
            qp.set('ano', ano);
            qp.set('mes', mes === '' ? 'todos' : mes);
            qp.set('dias', diasSel.length ? diasSel.join(',') : 'todos');
            if (lojasSel.length) qp.set('lojas', lojasSel.join(','));
            const qs = `?${qp.toString()}`;
            try {
                // Busca só o que a visão ativa precisa (a casca de filtro é a mesma).
                if (visao === 'rh') {
                    const r = await fetch(`/api/home/rh/painel${qs}`, { headers });
                    if (!vivo) return;
                    if (r.ok) setRhData(await r.json());
                } else {
                    const rV = await fetch(`/api/home/operacao/vendas${qs}`, { headers });
                    if (!vivo) return;
                    if (rV.ok) setVendas(await rV.json());
                }
            } catch (e) {
                console.error('[DashboardOperacao]', e);
            } finally {
                if (vivo) setCarregando(false);
            }
        })();
        return () => { vivo = false; };
        // Depende do APLICADO (`filtros`), não do rascunho: é isso que faz o "Aplicar"
        // ser o único gatilho de carga. Trocar de aba (visão) segue recarregando na
        // hora — não é filtro, é outra tela.
    }, [filtros, ano, mes, diasSel, lojasSel, visao]);

    // Aplicar = promove o rascunho a filtro vigente. Só quando há diferença: aplicar
    // o que já está na tela refaria as mesmas queries à toa.
    const aplicar = () => { if (sujo) setFiltros(rascunho); };

    // A grade de dias segue o mês do RASCUNHO (é o que o usuário está escolhendo
    // agora), não o aplicado — senão, ao trocar para um mês de 31 dias, a grade
    // continuaria mostrando os 28 do mês antigo até apertar Aplicar.
    // Ano view (mes='') → sem dia.
    const diasNoMes = rascunho.mes ? new Date(rascunho.ano, rascunho.mes, 0).getDate() : 0;
    // Troca de mês/ano zera a seleção de dias (evita dia inválido no novo mês).
    const trocarMes = (novoMes) => setRascunho((r) => ({ ...r, mes: novoMes, dias: [] }));
    const trocarAno = (novoAno) => setRascunho((r) => ({ ...r, ano: novoAno, dias: [] }));
    const dias = Array.from({ length: diasNoMes }, (_, i) => i + 1);

    // Dataset da visão ativa — alimenta o cabeçalho comum (anos, seletor de loja,
    // período). Operação e RH devolvem o mesmo formato de { escritorio, lojas,
    // focoLojas, mercadoLojas, anos, periodo }.
    const dados = visao === 'rh' ? rhData : vendas;
    const escritorio = dados?.escritorio;

    // Escritório abre com TODAS as lojas já marcadas (não o agregado "Rede" vazio),
    // para tirar uma loja do recorte ser um clique. Roda UMA vez, quando os dados
    // chegam trazendo a lista de lojas: pré-marca o rascunho E o aplicado — é o
    // aplicado que faz os cards já abrirem refletindo a seleção, sem exigir
    // "Aplicar". O ref trava em uma execução só; depois disso quem manda no filtro
    // é a pessoa (inclusive tirando lojas — não re-marcamos). Para quem é preso à
    // própria loja (escritorio=false) o efeito nunca dispara.
    const preSelecionouLojas = useRef(false);
    useEffect(() => {
        if (preSelecionouLojas.current) return;
        if (dados?.escritorio && Array.isArray(dados.lojas) && dados.lojas.length) {
            preSelecionouLojas.current = true;
            setRascunho((r) => ({ ...r, lojas: dados.lojas }));
            setFiltros((f) => ({ ...f, lojas: dados.lojas }));
        }
    }, [dados]);

    const foco = dados?.focoLojas || [];                  // [] = Rede
    // Descrição do recorte para a linha "Mostrando …": Rede · a própria loja · ou
    // "N lojas (ANV, FER, …)" para um grupo.
    const nomeFoco = foco.length === 0 ? 'Rede (todas as lojas)'
        : foco.length === 1 ? foco[0]
            : `${foco.length} lojas (${foco.join(', ')})`;
    const cards = vendas?.cards;

    const selectStyle = {
        fontFamily: FONTE_DADOS, fontSize: '13px', fontWeight: 600, color: COR.texto,
        border: `1px solid ${COR.bordaSuave}`, borderRadius: '8px', padding: '7px 12px',
        backgroundColor: '#fff', cursor: 'pointer', outline: 'none',
    };

    // `raizDashboard` define a família (e os algarismos tabulares) de TODO o
    // dashboard — texto e número no mesmo desenho, aqui e na visão RH abaixo.
    return (
        <div style={{ ...raizDashboard, marginBottom: '28px' }}>
            {/* Cabeçalho: só os FILTROS (período + loja). O que dizia em qual visão
                estamos é a barra de abas da Home, uma linha acima — por isso os
                filtros vão encostados à DIREITA, alinhados com a barra à esquerda. */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '12px', marginBottom: '18px' }}>
                {/* Filtro comum às duas visões (Operação e RH usam a mesma casca).
                    TODOS escrevem no RASCUNHO — nada consulta o servidor até o
                    "Aplicar" no fim da linha. Todos travam enquanto `carregando`:
                    mexer no meio da rodada dispara queries por cima das que já rodam.
                    ⚠️ A ABA, essa, deixou de travar durante a carga (11/09/2026): a
                    barra agora é da Home, que não sabe se o dashboard está carregando.
                    É seguro — o `vivo` do efeito descarta a resposta da aba abandonada —,
                    só desperdiça uma rodada de query em quem troca de aba na pressa. */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Período: Ano · Mês (Todos = ano inteiro) · Dia (Todos = mês inteiro) */}
                    <select value={rascunho.ano} onChange={(e) => trocarAno(Number(e.target.value))} disabled={carregando} style={{ ...selectStyle, ...(carregando ? selectTravado : null) }} title="Ano">
                        {(dados?.anos || [rascunho.ano]).map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <select value={rascunho.mes} onChange={(e) => trocarMes(e.target.value === '' ? '' : Number(e.target.value))} disabled={carregando} style={{ ...selectStyle, ...(carregando ? selectTravado : null) }} title="Mês">
                        <option value="">Ano todo</option>
                        {MESES.map((nome, i) => <option key={i} value={i + 1}>{nome}</option>)}
                    </select>
                    {/* Combobox multi-dias (só com mês): vazio = mês inteiro; marque
                        um ou vários dias avulsos. */}
                    {rascunho.mes !== '' && (
                        <MultiSelectDias dias={dias} selecionados={rascunho.dias} onChange={(d) => mexer('dias', d)} selectStyle={selectStyle} desabilitado={carregando} />
                    )}
                    {/* Loja = o FOCO da análise (multi-seleção). Vazio = Rede; marque
                        1+ lojas para ver a média por loja das marcadas vs o mercado. */}
                    {escritorio && (
                        <MultiSelectLojas lojas={dados?.lojas || []} selecionados={rascunho.lojas} onChange={(l) => mexer('lojas', l)} selectStyle={selectStyle} desabilitado={carregando} />
                    )}
                    <BotaoAplicar sujo={sujo} onClick={aplicar} desabilitado={carregando} />
                </div>
            </div>

            {/* Conteúdo do dashboard — enquanto as queries rodam, fica esmaecido e
                inerte sob o OverlayCarregando. O `minHeight` garante que o véu tenha
                onde aparecer no PRIMEIRO carregamento (sem dado ainda, o bloco teria
                altura quase zero e o spinner ficaria invisível). */}
            <div style={{ position: 'relative', minHeight: carregando ? '320px' : 0 }}>
            <div style={conteudoCarregando(carregando)}>
            {/* Contexto comum (loja + período resolvido pelo backend); a nota de YoY é só da Operação.
                O chip de atualização é empurrado para a DIREITA da linha (Julia,
                2026-07-21): ele responde "esse número está velho?", uma pergunta que
                vale para a tela inteira — encostado no fim da linha ele deixa de
                competir com a leitura do recorte e fica alinhado com os filtros logo
                acima. `flex-wrap` para, em tela estreita, ele descer em vez de espremer
                o texto. */}
            <div style={{ fontSize: '13px', color: '#888', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ flex: 1, minWidth: '240px' }}>
                    Mostrando <strong style={{ color: COR.texto }}>{carregando ? '…' : nomeFoco}</strong>
                    {' · '}<strong style={{ color: COR.texto }}>{dados?.periodo?.label || '…'}</strong>
                    {visao === 'operacao' && ' · comparado com o mesmo período do ano anterior.'}
                    {dados?.periodo?.vazio && <span style={{ color: '#c05', marginLeft: '6px' }}>— período no futuro, sem dados.</span>}
                </span>
                {/* Selo de atualização dos dados da aba. O backend manda só a DATA
                    (Operação = último dia de venda carregado pelo ETL; RH = dia da
                    última recarga da dtbConfere) e o rótulo é escrito aqui, UM só para
                    as duas abas (Julia, 2026-07-21): o gestor lê a mesma frase no mesmo
                    lugar, sem precisar interpretar "até" vs "em".
                    Chip (e não texto cinza solto): é a informação que responde "esse
                    número está velho?" — precisa saltar aos olhos na linha de contexto. */}
                {dados?.atualizacao?.data && (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '3px 9px', borderRadius: '999px',
                        background: COR.amareloFundo, border: `1px solid ${COR.amarelo}`,
                        color: COR.texto, fontSize: '12px', fontWeight: 700,
                        whiteSpace: 'nowrap', flexShrink: 0,
                    }}>
                        <RefreshCw size={12} color={COR.vermelhoEscuro} />
                        Última atualização: {dados.atualizacao.data}
                    </span>
                )}
            </div>

            {visao === 'rh' ? <DashboardRH dados={rhData} /> : (
                <>
                    {/* Bloco 1 — Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px', marginBottom: '26px' }}>
                        <CardIndicador icone={<ShoppingCart size={18} />} cor={COR.vermelhoEscuro} rotulo="VENDA"
                            valor={fmtMoedaCompacta(cards?.venda?.valor)} evolYoY={cards?.venda?.evolYoY}
                            mercadoEvol={cards?.venda?.mercadoEvolYoY} />
                        {vendas?.projecao && <CardProjecao projecao={vendas.projecao} />}
                        {vendas?.orcamento && <CardOrcamento orcamento={vendas.orcamento} />}
                        <CardIndicador icone={<Users size={18} />} cor={COR.amarelo} rotulo="GUEST COUNT (GC)"
                            valor={fmtNumCompacto(cards?.gc?.valor)} evolYoY={cards?.gc?.evolYoY}
                            mercadoEvol={cards?.gc?.mercadoEvolYoY} />
                        <CardIndicador icone={<Receipt size={18} />} cor={COR.vermelhoEscuro} rotulo="TICKET MÉDIO"
                            valor={fmtMoeda2(cards?.ticketMedio?.valor)} evolYoY={cards?.ticketMedio?.evolYoY}
                            mercadoEvol={cards?.ticketMedio?.mercadoEvolYoY} />
                    </div>

                    {/* Bloco 2 — Tabela por segmento */}
                    <div style={{ marginBottom: '26px' }}>
                        <div style={{ marginBottom: '12px' }}>
                            <TituloBloco icone={<PieChart size={18} />} cor={COR.vermelhoEscuro} texto="Resumo por segmento" />
                        </div>
                        {vendas?.segmentos ? <TabelaSegmentos segmentos={vendas.segmentos} /> : <div style={{ color: '#999', fontSize: '13px' }}>Carregando…</div>}
                    </div>

                </>
            )}
            </div>
            {carregando && <OverlayCarregando />}
            </div>
        </div>
    );
}
